import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import type { AxiosRequestConfig } from 'axios';
import localforage from 'localforage';
import { isEqual, isNil } from 'lodash';

import type { FetchHookReturnType } from '~/components/useDataFetcher/types';
import { dataFetchReducer, fetchData } from '~/components/useDataFetcher/utils';
import { CACHE_TTL } from '~/Utils';

type UseCachedDataFetcherOptions = {
  url: string | null;
  config?: AxiosRequestConfig;
  shouldFetch?: boolean;
  localCacheKey?: string;
  shouldUseCache?: boolean;
};

type FetchParams = [string | null, AxiosRequestConfig];

const updateCacheData = <T>(localCacheKey: string, data: T, httpStatus: number) => {
  // Never cache a nullish payload: a success with no body would otherwise be replayed on the next load as
  // `FETCH_SUCCESS` with `data: undefined`, which consumers read as "loaded, no error" and then crash when
  // they use the missing data.
  if (localCacheKey && !isNil(data)) {
    localforage.setItem(localCacheKey, {
      timestamp: Date.now(),
      data,
      httpStatus,
    });
  }
};

const useCachedDataFetcher = <T = unknown>({
  url,
  config = {},
  shouldFetch = true,
  localCacheKey = '',
  shouldUseCache = false,
}: UseCachedDataFetcherOptions): FetchHookReturnType<T> => {
  const lastParamsRef = useRef<FetchParams>([url, config]);
  const [currParams, setCurrParams] = useState<FetchParams>([url, config]);
  const cacheStateRef = useRef({ wasUpdated: false, key: localCacheKey });

  const updateCacheDataIfNeeded = useCallback(
    (data: T, httpStatus: number) => {
      if (shouldUseCache && localCacheKey) {
        updateCacheData(localCacheKey, data, httpStatus);
        if (cacheStateRef.current.key === localCacheKey) {
          cacheStateRef.current.wasUpdated = true;
        }
      }
    },
    [localCacheKey, shouldUseCache]
  );

  const reloadData = useCallback(async () => {
    const [currUrl, currConfig] = currParams;

    const action = await fetchData<T>(currUrl, currConfig, true);
    if (action) {
      dispatch(action);

      if (action.type === 'FETCH_SUCCESS') {
        updateCacheDataIfNeeded(action.payload, action.httpStatus);
        return action.payload;
      }
    }
  }, [currParams, updateCacheDataIfNeeded]);

  const [state, dispatch] = useReducer(dataFetchReducer<T>, {
    isLoading: shouldFetch,
    isError: false,
    data: undefined,
  });

  useEffect(() => {
    cacheStateRef.current = { wasUpdated: false, key: localCacheKey };
  }, [localCacheKey]);

  useEffect(() => {
    const [currUrl, currConfig] = currParams;

    let didCancel = false;
    const callFetchData = async () => {
      dispatch({ type: 'FETCH_INIT' });
      const action = await fetchData<T>(currUrl, currConfig);
      if (!didCancel) {
        dispatch(action);

        if (action.type === 'FETCH_SUCCESS') {
          updateCacheDataIfNeeded(action.payload, action.httpStatus);
        }
      }
    };
    if (shouldFetch) {
      callFetchData();
    }
    return () => {
      didCancel = true;
    };
  }, [currParams, shouldFetch, updateCacheDataIfNeeded]);

  useEffect(() => {
    if (lastParamsRef.current) {
      const [lastUrl, lastConfig] = lastParamsRef.current;
      if (lastUrl === url && isEqual(lastConfig, config)) {
        return;
      }
    }
    setCurrParams([url, config]);
    lastParamsRef.current = [url, config];
  }, [url, config]);

  useEffect(() => {
    let didCancel = false;
    if (shouldUseCache && localCacheKey) {
      localforage.getItem<{ timestamp: number; data: T; httpStatus: number }>(localCacheKey).then((cachedData) => {
        if (didCancel || cacheStateRef.current.wasUpdated) return;
        // Ignore a stale or nullish-payload entry: replaying `data: undefined` as a success would let the
        // network fetch be skipped and hand consumers a "loaded, no error, but empty" state.
        if (cachedData && !isNil(cachedData.data) && Date.now() - cachedData.timestamp < CACHE_TTL) {
          dispatch({ type: 'FETCH_SUCCESS', payload: cachedData.data, httpStatus: cachedData.httpStatus });
        }
      });
    }
    return () => {
      didCancel = true;
    };
  }, [localCacheKey, shouldUseCache]);

  return { ...state, reloadData };
};

export default useCachedDataFetcher;
